import { FC, useCallback, useEffect } from 'react';
import { ModalProps } from './types';
import clsx from 'clsx';
import Card, { CardBody, CardFooter, CardHeader } from '../Card';
import IconClose from '../../icons/IconClose';
import IconButton from '../IconButton';
import { Transition } from '@headlessui/react';

/**
 *  UI Component
 */
const Modal: FC<ModalProps> = (props) => {
  // Apply Default props values
  props = {
    ...{
      size: 'normal'
    },
    ...props
  };

  const {
    open,
    onClose,
    children,
    title,
    className,
    headerIcon,
    actions,
    hideHeader,
    notDismissible,
    cardProps,
    cardHeaderProps,
    cardBodyProps,
    cardFooterProps
  } = props;

  const handleClose = () => {
    if (!notDismissible && onClose) onClose();
  };

  // This is to lock the screen while the modal is open
  useEffect(() => {
    if (open) {
      document.querySelector('body')?.classList.add('has-open-modal');
    } else {
      document.querySelector('body')?.classList.remove('has-open-modal');
    }
  }, [open]);

  // Escape key handling
  const handleEscKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !notDismissible && onClose && open) {
        onClose();
      }
    },
    [notDismissible, onClose, open]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscKey, false);

    return () => {
      document.removeEventListener('keydown', handleEscKey, false);
    };
  }, [handleEscKey]);

  // Card component props
  const cls = styleModal(props);

  const cardPropClassName = cardProps?.className;
  if (cardProps?.className) {
    delete cardProps.className;
  }
  const elCardProps = {
    seperated: false,
    rounded: false,
    ...cardProps
  };
  const elCardFooterProps = {
    dim: true,
    ...cardFooterProps
  };
  return (
    <Transition show={open} as="div" className={clsx(cls.main)}>
      <Transition.Child
        as="div"
        enter="transition duration-1000 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        className={clsx(cls.backdrop)}
        onClick={handleClose}
      ></Transition.Child>
      <Transition.Child
        as={Card}
        enter="transition duration-200 ease-out-back"
        enterFrom="opacity-0 translate-y-1/3"
        enterTo="opacity-100 translate-y-0"
        leave="transition duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-1/3"
        className={clsx(cls.card, cardPropClassName)}
        {...elCardProps}
      >
        {!hideHeader && (
          <CardHeader
            icon={headerIcon}
            heading={title}
            className={className}
            actions={
              !notDismissible && (
                <IconButton
                  variant="outlined"
                  size="small"
                  onClick={handleClose}
                >
                  <IconClose />
                </IconButton>
              )
            }
            {...cardHeaderProps}
          />
        )}
        <CardBody {...cardBodyProps}>{children}</CardBody>
        {actions && <CardFooter {...elCardFooterProps}>{actions}</CardFooter>}
      </Transition.Child>
    </Transition>
  );
};

export default Modal;

function styleModal(props: ModalProps) {
  const mc = 'Modal';
  const mcBackdrop = `${mc}__Backdrop`;
  const mcCard = `${mc}__Card`;
  const cls = {
    main: [mc],
    backdrop: [mcBackdrop],
    card: [mcCard]
  };
  if (!props.open) {
    cls.main.push(`${mc}--hidden`);
  }
  if (props.size) {
    cls.main.push(`${mc}--${props.size}`);
  }
  return cls;
}
